import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  Anchor,
  Truck,
  ShieldCheck,
  Compass,
  ArrowRight,
  ArrowUpRight,
  Star,
} from "lucide-react";
import { LogoMark, Wordmark } from "@/components/Logo";
import { FadeUp, LineReveal, WordCascade } from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE =
  "https://images.pexels.com/photos/13191812/pexels-photo-13191812.jpeg?auto=compress&cs=tinysrgb&w=2400";
const RAMP_IMAGE =
  "https://images.pexels.com/photos/13191811/pexels-photo-13191811.jpeg?auto=compress&cs=tinysrgb&w=1600";
const ROAD_IMAGE =
  "https://images.pexels.com/photos/12417699/pexels-photo-12417699.jpeg?auto=compress&cs=tinysrgb&w=1600";

const MARQUEE = [
  "Boats", "Fifth-Wheels", "Class A", "Wakeboats", "Pontoons",
  "Toy Haulers", "Travel Trailers", "Class C", "Center Consoles", "Sailboats",
];

const HOW = [
  {
    n: "01",
    title: "Post your haul",
    desc: "Dimensions, hitch class, pickup + delivery cities, optional photo. Free to post — takes about 90 seconds.",
  },
  {
    n: "02",
    title: "Drivers compete",
    desc: "Verified, insured, DOT-certified drivers submit sealed bids with their price and message.",
  },
  {
    n: "03",
    title: "Accept & roll",
    desc: "Pick the bid you like. Chat in a moderated inbox. Pay the driver direct — we only take a small success fee.",
  },
];

const WHY = [
  {
    icon: ShieldCheck,
    title: "Vetted, insured, DOT-approved",
    desc: "Every driver uploads their license, active insurance certificate, and DOT number. Reviewed by real humans before their first bid.",
  },
  {
    icon: Compass,
    title: "Nationwide coverage",
    desc: "From Alaskan ferry drops to Florida launches — 50 states, no dead zones. Match with drivers on the exact corridors you need.",
  },
  {
    icon: Anchor,
    title: "Built for actual trailers",
    desc: "Weight, length, hitch class, low-clearance flags. This isn't a rideshare with a truck emoji — it's a haulers-first marketplace.",
  },
  {
    icon: Star,
    title: "Public reputation",
    desc: "0–100% ratings on both sides after every job. Long-term trust that means fair prices for shippers and repeat work for drivers.",
  },
];

export default function Landing() {
  const [drivers, setDrivers] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const heroOverlayOpacity = useTransform(scrollYProgress, [0, 0.7], [0.55, 0.9]);
  const heroCopyY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
    axios
      .get(`${API}/drivers?limit=6`)
      .then((r) => setDrivers(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="landing-page">
      {/* NAV */}
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="fixed top-0 left-0 right-0 z-40 px-6 md:px-10 lg:px-14 py-5 flex items-center justify-between backdrop-blur-md bg-black/30 border-b border-white/[0.06]"
        data-testid="top-nav"
      >
        <Link to="/" className="flex items-center gap-3">
          <LogoMark size={36} />
          <Wordmark className="text-xl md:text-2xl" />
        </Link>
        <div className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.22em] font-semibold text-zinc-300">
          <a href="#how" className="hover:text-white transition-colors">How it works</a>
          <a href="#why" className="hover:text-white transition-colors">Why us</a>
          <Link to="/drivers" className="hover:text-white transition-colors">Drivers</Link>
          <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/signup"
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold bg-[#FF3B30] hover:bg-[#D62828] text-white px-4 md:px-5 py-2.5 md:py-3 transition-all"
          >
            Start free <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </motion.nav>

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-screen w-full overflow-hidden flex items-center"
        data-testid="hero-section"
      >
        {/* Parallax background image */}
        <motion.div
          style={{ y: bgY, scale: bgScale }}
          className="absolute inset-0 z-0 will-change-transform"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
            aria-hidden="true"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroOverlayOpacity }}
          className="absolute inset-0 bg-black"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="absolute inset-0 diagonal-stripes opacity-40 pointer-events-none" />

        <motion.div
          style={{ y: heroCopyY }}
          className="relative z-10 w-full px-6 md:px-10 lg:px-14 pt-32 pb-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
        >
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="inline-flex items-center gap-3 border-l-4 border-[#FF3B30] pl-3 py-1 mb-6"
              data-testid="hero-tagline"
            >
              <span className="text-[11px] uppercase tracking-[0.35em] text-[#FF3B30] font-bold">
                Boat &amp; RV Transport Marketplace
              </span>
              <span className="hidden sm:inline text-[11px] uppercase tracking-[0.35em] text-zinc-500">
                / 50 states
              </span>
            </motion.div>
            <h1
              className="font-display font-black uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl xl:text-[7.5rem] mb-6"
              data-testid="hero-title"
            >
              <LineReveal
                text={"The marketplace\nfor boats,\nRVs & trailers."}
                delay={0.35}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="text-base md:text-lg text-zinc-300 max-w-xl leading-relaxed mb-8"
              data-testid="hero-subtitle"
            >
              Post your haul in 90 seconds. Verified, insured drivers compete on price.
              You pay the driver direct — we only take a small success fee.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="flex flex-wrap items-center gap-3 mb-8"
            >
              <Link
                to="/signup?role=shipper"
                data-testid="hero-cta-shipper"
                className="group inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-sm border-2 border-[#FF3B30] hover:border-[#D62828] py-4 px-7 transition-all"
              >
                Ship a boat or RV
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/driver-signup"
                data-testid="hero-cta-driver"
                className="group inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white font-bold uppercase tracking-widest text-sm py-4 px-7 transition-all"
              >
                Drive jobs
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.3 }}
              className="flex flex-wrap items-center gap-6"
            >
              <div className="flex items-center gap-3">
                <Anchor className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-zinc-300">Boats</span>
              </div>
              <div className="w-px h-4 bg-white/15" />
              <div className="flex items-center gap-3">
                <Truck className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-zinc-300">RVs</span>
              </div>
              <div className="w-px h-4 bg-white/15" />
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-zinc-300">Trailers</span>
              </div>
            </motion.div>
          </div>

          {/* Right: entry points — no waitlist; accounts open instantly */}
          <div className="lg:col-span-5" id="signup">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#0F0F10]/85 backdrop-blur-md border border-white/[0.08] p-7 md:p-9 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]"
              data-testid="get-started-card"
            >
              <div className="mb-6">
                <div className="inline-block bg-[#FF3B30] text-black text-[10px] uppercase tracking-[0.28em] font-black px-2 py-1 mb-4">
                  Free to join
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-black uppercase leading-tight">
                  Start today.
                  <br />
                  <span className="text-[#FF3B30]">Ship or drive.</span>
                </h2>
                <p className="text-sm text-zinc-400 mt-3">
                  No waitlist, no invite codes. Post your first haul in 90 seconds — or get DOT-verified and start bidding on jobs.
                </p>
              </div>
              <div className="space-y-3">
                <Link
                  to="/signup?role=shipper"
                  data-testid="card-cta-shipper"
                  className="group w-full bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-4 px-6 flex items-center justify-center gap-3 transition-all"
                >
                  Ship a boat or RV
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/driver-signup"
                  data-testid="card-cta-driver"
                  className="group w-full border-2 border-white/25 hover:border-white text-white font-bold uppercase tracking-widest py-4 px-6 flex items-center justify-center gap-3 transition-all"
                >
                  Become a driver
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </Link>
                <p className="text-[11px] text-zinc-500 leading-relaxed pt-1 text-center">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#FF3B30] hover:text-white transition-colors underline underline-offset-2">
                    Sign in
                  </Link>
                  .
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-zinc-500">
          <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
          <motion.div
            className="w-px h-10 bg-gradient-to-b from-[#FF3B30] to-transparent"
            animate={{ scaleY: [0.4, 1, 0.4] }}
            style={{ transformOrigin: "top" }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative bg-[#0A0A0A] border-y border-white/[0.06] py-6 overflow-hidden" data-testid="marquee">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((w, i) => (
            <span
              key={i}
              className="mx-8 text-3xl md:text-5xl font-display font-black uppercase tracking-tight text-zinc-800 hover:text-[#FF3B30] transition-colors"
            >
              {w}
              <span className="text-[#FF3B30] mx-8">/</span>
            </span>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative bg-[#0A0A0A] px-6 md:px-10 lg:px-14 py-24 lg:py-32" data-testid="how-section">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="mb-16 max-w-3xl">
              <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ How it works</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] mt-4">
                <WordCascade text="Three steps. No middlemen." />
              </h2>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
            {HOW.map((step, i) => (
              <FadeUp key={step.n} delay={i * 0.12}>
                <div className="bg-[#0A0A0A] p-8 lg:p-10 h-full">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold mb-6">{step.n}</div>
                  <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-3 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — split image + editorial */}
      <section id="why" className="relative bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="why-section">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <FadeUp>
            <div
              className="min-h-[500px] lg:min-h-[720px] bg-cover bg-center relative"
              style={{ backgroundImage: `url('${RAMP_IMAGE}')` }}
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/60" />
              <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF3B30] font-bold">/ Real haulers</div>
                <div className="font-display text-2xl md:text-3xl font-black uppercase text-white mt-2 max-w-xs leading-tight">
                  "First job paid off my platform fee in a week."
                </div>
              </div>
            </div>
          </FadeUp>
          <div className="px-6 md:px-10 lg:px-16 py-16 lg:py-24">
            <FadeUp>
              <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Why us</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] mt-4 mb-10">
                Not a rideshare.
                <br />
                A <span className="text-[#FF3B30]">haulers-first</span> platform.
              </h2>
            </FadeUp>
            <div className="space-y-6">
              {WHY.map((w, i) => {
                const Icon = w.icon;
                return (
                  <FadeUp key={w.title} delay={i * 0.08}>
                    <div className="flex gap-5 border-t border-white/[0.08] pt-5">
                      <div className="w-10 h-10 border border-[#FF3B30] flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#FF3B30]" strokeWidth={2.2} />
                      </div>
                      <div>
                        <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight leading-tight">
                          {w.title}
                        </h3>
                        <p className="text-sm text-zinc-400 leading-relaxed mt-2">{w.desc}</p>
                      </div>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED DRIVERS */}
      <section className="relative bg-[#0A0A0A] border-t border-white/[0.06] px-6 md:px-10 lg:px-14 py-24 lg:py-32" data-testid="drivers-section">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Featured this week</span>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] mt-4">
                  Meet the top-rated haulers.
                </h2>
              </div>
              <Link
                to="/drivers"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white transition-colors border-b border-[#FF3B30] pb-1"
                data-testid="see-all-drivers"
              >
                See all drivers <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </FadeUp>

          {drivers.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="border border-white/[0.06] p-6 bg-[#0F0F10] animate-pulse h-56" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drivers.slice(0, 6).map((d, i) => (
                <FadeUp key={d.id} delay={i * 0.08}>
                  <Link
                    to={`/drivers/${d.id}`}
                    data-testid={`featured-driver-${d.id}`}
                    className="group block border border-white/[0.08] bg-[#0F0F10] p-6 hover:border-[#FF3B30] transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#FF3B30] text-black font-display text-2xl font-black flex items-center justify-center uppercase">
                        {d.name?.slice(0, 2) || "DR"}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-display text-lg font-black uppercase truncate">{d.name}</div>
                          {d.verified && (
                            <ShieldCheck className="w-4 h-4 text-[#FF3B30] shrink-0" strokeWidth={2.5} />
                          )}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mt-1">
                          {d.verified ? "Verified driver" : "New driver"}
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 flex items-center gap-4 text-sm text-zinc-400">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-[#FF3B30]" fill="#FF3B30" />
                        <span className="font-semibold text-white">
                          {d.rating_avg ? d.rating_avg.toFixed(1) : "—"}
                        </span>
                        <span className="text-zinc-500">({d.rating_count})</span>
                      </div>
                      <div className="w-px h-3 bg-white/15" />
                      <span>{d.completed_jobs} completed</span>
                    </div>
                    {d.bio && (
                      <p className="text-sm text-zinc-500 leading-relaxed mt-4 line-clamp-3">{d.bio}</p>
                    )}
                    <div className="mt-6 text-[11px] uppercase tracking-widest text-[#FF3B30] font-bold flex items-center gap-2 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                      View passport <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#FF3B30] text-black px-6 md:px-10 lg:px-14 py-14" data-testid="stats-section">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: "50", v: "States" },
            { k: "100%", v: "Vetted drivers" },
            { k: "0%", v: "Driver commission" },
            { k: "72h", v: "Avg bids in" },
          ].map((s, i) => (
            <FadeUp key={s.v} delay={i * 0.08}>
              <div className="text-left">
                <div className="font-display text-5xl md:text-6xl font-black uppercase leading-none">{s.k}</div>
                <div className="text-[11px] md:text-xs uppercase tracking-widest font-bold mt-2 opacity-80">{s.v}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SECONDARY CTA — with parallax road bg */}
      <SecondaryCTA />

      {/* FOOTER */}
      <footer className="bg-black border-t border-white/[0.06] px-6 md:px-10 lg:px-14 py-10" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark size={28} />
            <Wordmark className="text-lg" />
          </Link>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500">
            © {new Date().getFullYear()} boatrvmoving · The marketplace for boat &amp; RV transport.
          </p>
          <div className="flex items-center gap-5 text-[11px] uppercase tracking-widest text-zinc-400">
            <Link to="/drivers" className="hover:text-[#FF3B30] transition-colors">Drivers</Link>
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-[#FF3B30] transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#FF3B30] transition-colors">Terms</Link>
            <Link to="/login" className="hover:text-[#FF3B30] transition-colors">Sign in</Link>
            <a href="mailto:hello@boatrvmoving.com" data-testid="footer-email" className="hover:text-[#FF3B30] transition-colors">
              hello@boatrvmoving.com
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}

function SecondaryCTA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section
      ref={ref}
      className="relative bg-[#0A0A0A] overflow-hidden border-t border-white/[0.06]"
      data-testid="secondary-cta"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0 bg-cover bg-center opacity-25"
        aria-hidden="true"
      >
        <div className="absolute inset-0" style={{ backgroundImage: `url('${ROAD_IMAGE}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative max-w-4xl mx-auto text-center px-6 md:px-10 py-24 lg:py-36">
        <FadeUp>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black uppercase leading-[0.95]">
            Post a haul.
            <br />
            <span className="text-[#FF3B30]">Get bids by tomorrow.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto text-base md:text-lg">
            Free to post. Verified drivers. Zero platform lock-in.
          </p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup?role=shipper"
              data-testid="secondary-cta-button"
              className="inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-4 px-9 transition-all"
            >
              Ship a boat or RV <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/driver-signup"
              className="inline-flex items-center gap-3 border-2 border-white/25 hover:border-white text-white font-bold uppercase tracking-widest py-4 px-9 transition-all"
            >
              Become a driver <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
